import axios, { AxiosInstance } from "axios";
import jwtDecode from "jwt-decode";

import { User } from "./resources/iam/User";

interface Config {
  baseUrl: string;
}

const REFRESH_TIMING_MARGIN = 30000; // How many ms before the JWT expiration the refresh request should be sent

export interface CurrentUser {
  email: string;
  id: number;
  groups: {
    uuid: string;
    name: string;
  }[];
  profile: {
    firstName: string;
    lastName: string;
  } | null;
}

export default class MegaraxClient {
  private axiosClient!: AxiosInstance;
  private jwt?: string;
  private expirationTime?: number;

  private googleIdTokenInProvider?: () => Promise<string> | string;

  user?: CurrentUser;

  get signedIn(): boolean {
    return !!this.jwt;
  }

  setErrorHandler(handler: (errors: any, statusCode?: any) => any): void {
    this.axiosClient.interceptors.response.use(
      (resp) => resp,
      (error) => {
        handler(error.response.data.errors, error.response.status);
        throw error;
      },
    );
  }

  get: AxiosInstance["get"];
  post: AxiosInstance["post"];
  patch: AxiosInstance["patch"];
  put: AxiosInstance["put"];
  delete: AxiosInstance["delete"];

  authenticatedRequest = <T extends (...params: any[]) => Promise<any>>(requestMethod: T) =>
    (async (...params: Parameters<T>) => {
      if (!this.expirationTime || this.expirationTime - REFRESH_TIMING_MARGIN < Date.now()) await this.authenticate();
      return await requestMethod(...params);
    }) as T;

  constructor(config: Config) {
    this.axiosClient = axios.create({
      baseURL: config.baseUrl,
    });

    this.axiosClient.interceptors.request.use((request) => {
      console.log("Megarax request", request);
      return request;
    });

    this.get = this.authenticatedRequest(this.axiosClient.get);
    this.post = this.authenticatedRequest(this.axiosClient.post);
    this.put = this.authenticatedRequest(this.axiosClient.put);
    this.delete = this.authenticatedRequest(this.axiosClient.delete);
    this.patch = this.authenticatedRequest(this.axiosClient.patch);
  }

  async signIn(googleIdTokenInProvider: () => Promise<string> | string) {
    this.googleIdTokenInProvider = googleIdTokenInProvider;
    return await this.authenticate();
  }

  private async authenticate() {
    if (!this.googleIdTokenInProvider) throw "No token provider specified!";
    const googleIdToken = await this.googleIdTokenInProvider();
    return await this.signInWithGoogle(googleIdToken);
  }

  private async signInWithGoogle(idToken: string): Promise<CurrentUser> {
    this.axiosClient.defaults.headers = {};
    this.setJwt(idToken);

    // succesful backend response

    await Promise.all([
      // get user
      this.axiosClient.get("/iam/me").then((response) => {
        this.user = response.data;
      }),
    ]);

    return this.user!;
  }

  signOut() {
    this.jwt = undefined;
    this.user = undefined;
    this.googleIdTokenInProvider = undefined;
  }

  private async setJwt(jwt: string) {
    this.jwt = jwt;

    this.axiosClient.defaults.headers = { Authorization: jwt };
    const parsedToken = jwtDecode<any>(jwt);
    this.expirationTime = parsedToken.exp * 1000;
  }
}

export { MegaraxClient };
